import React, { useCallback, useEffect, useReducer, useState } from 'react';
import classNames from 'classnames';

import { Route, Switch, useHistory } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CaretRightFilled,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  LoadingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { Button, Input, Tooltip } from '@mm/components';
import validator from 'validator';

import css from './SetupForm.module.css';
import { useRouteMatch } from 'react-router';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import {
  hasConfiguredEvents,
  hasConfiguredFb,
  selectXRayConfig,
} from '@mm/state/selectors/xrayConfigSelectors';
import {
  errorToast,
  successToast,
} from '../../../../../../common/helpers/toast';
import { omit } from 'lodash';
import { isDefaultFacebookConversionApiEventId } from '@mm/state/helpers/capi-helpers';
import isURL = validator.isURL;

type SetupFormProps = {
  className?: string;
};

const steps = {
  events: {
    path: '',
    number: '1',
    title: 'Events',
    description: 'Select what events you want to send to Facebook',
  },
  fbSettings: {
    path: '/facebook-settings',
    title: 'Facebook Settings',
    description:
      'In order to send event data to Facebook, we need the following information from your Facebook account.',
    number: '2',
  },
  goLive: {
    path: '/start',
    title: 'Go Live',
    description:
      'We recommend testing your configuration before getting started, but you can continue without testing.',
    number: '3',
  },
};

const emptyEventsConfig = {
  viewContent: '',
  viewItem: '',
  search: '',
  addToCart: '',
  initiateCheckout: '',
  addPaymentInfo: '',
  purchase: '',
  subscriptionPurchase: '',
  completeRegistration: '',
  subscribe: '',
};

export const facebookConversionApiEvents = [
  {
    key: 'viewContent',
    title: 'View Content',
    info: 'Standard Page view events should just include your website URL. Enter your website URL.',
    placeholder: 'Add URL',
  },
  {
    key: 'viewItem',
    title: 'View Item',
    info: (
      <>
        Enter the URL for a product view. Typically this is{' '}
        <strong>yourwebsite.com/product</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'addToCart',
    title: 'Add to Cart',
    info: (
      <>
        Enter the URL for a cart view. Typically this is{' '}
        <strong>yourwebsite.com/cart</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'initiateCheckout',
    title: 'Initiate Checkout',
    info: (
      <>
        Enter the URL for a checkout page. Typically this is{' '}
        <strong>yourwebsite.com/checkout</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'addPaymentInfo',
  //   title: 'Add Payment Info',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'purchase',
    title: 'Purchase',
    info: (
      <>
        Enter the URL for a completed purchase. Typically this is{' '}
        <strong>yourwebsite.com/thankyou</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'subscriptionPurchase',
  //   title: 'Subscription Purchase',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'search',
    title: 'Search',
    info: 'Enter the URL for a search result.',
    placeholder: 'Add URL',
  },
  {
    key: 'completeRegistration',
    title: 'Complete Registration',
    info: 'Enter the URL for a account sign up or account creation page.',
    placeholder: 'Add URL',
  },
  // {
  //   key: 'subscribe',
  //   title: 'Subscribe',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
];

export const facebookConversionApiVisibleEventsIds =
  facebookConversionApiEvents.map(v => v.key);

const EventsStep = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();

  const defaultConfiguration = useSelector(selectXRayConfig);

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const [busy, setBusy] = useState(false);
  const [addNewEventVisible, setAddNewEventVisible] = useState(false);

  const [newEventName, setNewEventName] = useState('');
  const [newEventUrl, setNewEventUrl] = useState('');

  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'change':
          return {
            ...state,
            [action.payload.key]: action.payload.value,
          };
        case 'remove':
          return omit(state, [action.payload.key]);
        default:
          return state;
      }
    },
    defaultConfiguration?.configurations || {},
    state => state,
  );

  const handleTurnOffNewFieldForm = () => {
    setAddNewEventVisible(false);
    setNewEventUrl('');
    setNewEventName('');
  };

  const handleTurnOnNewFieldForm = () => {
    setAddNewEventVisible(true);
  };

  const handleNewFieldCheckMarkBtnClick = () => {
    dispatch({
      type: 'change',
      payload: { key: newEventName, value: newEventUrl },
    });

    handleTurnOffNewFieldForm();
  };

  const isValidByKey = key => isURL(String(state[key]));

  const isValidByValue = val => isURL(val);

  const handleUrlChange = (key, value) => {
    dispatch({ type: 'change', payload: { key, value } });
  };

  const handleRemoveField = key => {
    dispatch({ type: 'remove', payload: { key } });
  };

  const handleSave = async () => {
    setBusy(true);

    try {
      const res =
        await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
          chatbotId,
          {
            ...defaultConfiguration,
            configurations: state,
          },
        );
      if (hasConfiguredEvents(res)) {
        history.push(
          history.location.pathname +
            (history.location.pathname.endsWith('/') ? '' : '/') +
            'facebook-settings',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const customEvents = Object.keys(state)
    .filter(k => !isDefaultFacebookConversionApiEventId(k))
    .map(key => ({
      key,
      title: key,
      info: '',
      placeholder: '',
    }));

  const eventsSource = [...facebookConversionApiEvents, ...customEvents];

  return (
    <div className={css.card}>
      <table className={css.table}>
        <thead>
          <tr>
            <th scope="col">Events</th>
            <th scope="col">URL contains</th>
          </tr>
        </thead>

        <tbody>
          {eventsSource.map(event => (
            <tr key={event.key}>
              <th scope="row">
                <div className={css.eventCell}>
                  {event.title}

                  {isDefaultFacebookConversionApiEventId(event.key) && (
                    <Tooltip
                      css={{ container: css.tooltip }}
                      color="dark"
                      position="bottom"
                      align="center"
                      useHover
                      withoutLine
                      anchorElement={
                        <QuestionCircleOutlined className={css.infoIcon} />
                      }
                    >
                      {event.info}
                    </Tooltip>
                  )}
                </div>
              </th>
              <td>
                <div className={css.eventField}>
                  <Input
                    type="text"
                    onChange={e =>
                      handleUrlChange(event.key, e.target.value.trim())
                    }
                    disabled={busy}
                    invalid={state[event.key] && !isValidByKey(event.key)}
                    value={state[event.key]}
                    placeholder={event.placeholder}
                    css={{ container: css.input }}
                  />

                  {!isDefaultFacebookConversionApiEventId(event.key) && (
                    <div className={css.eventActions}>
                      <Button
                        onClick={() => handleRemoveField(event.key)}
                        color="secondary"
                        css={{ container: css.deleteEventBtn }}
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}

          {addNewEventVisible && (
            <>
              <tr key="new-event">
                <th scope="row">
                  <div className={css.eventCell}>
                    <Input
                      type="text"
                      onChange={e => setNewEventName(e.target.value.trim())}
                      disabled={busy}
                      invalid={!newEventName}
                      value={newEventName}
                      placeholder="Event name"
                      css={{ container: css.input }}
                    />
                  </div>
                </th>
                <td>
                  <div className={css.eventField}>
                    <Input
                      type="text"
                      onChange={e => setNewEventUrl(e.target.value.trim())}
                      disabled={busy}
                      invalid={!!newEventUrl && !isValidByValue(newEventUrl)}
                      value={newEventUrl}
                      placeholder="Add Url"
                      css={{ container: css.input }}
                    />

                    <div className={css.eventActions}>
                      <Button
                        disabled={
                          !newEventUrl ||
                          (!!newEventUrl && !isValidByValue(newEventUrl))
                        }
                        color="green"
                        css={{ container: css.saveEventBtn }}
                        onClick={handleNewFieldCheckMarkBtnClick}
                      >
                        <CheckOutlined />
                      </Button>

                      <Button
                        onClick={() => handleTurnOffNewFieldForm()}
                        color="secondary"
                        css={{ container: css.deleteEventBtn }}
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      <Button
        css={{ container: css.addNewEventBtn }}
        color="secondary-blue"
        onClick={() => handleTurnOnNewFieldForm()}
        disabled={addNewEventVisible}
        leftIcon={<PlusOutlined />}
      >
        Event
      </Button>

      <Button
        css={{ container: css.submitBtn }}
        onClick={handleSave}
        disabled={
          busy ||
          addNewEventVisible ||
          Object.keys(state)
            .filter(k => state[k])
            .some(key => !isValidByKey(key)) ||
          (!hasConfiguredEvents(defaultConfiguration)
            ? Object.values(state).every(v => !v)
            : undefined)
        }
      >
        Save & Continue{' '}
        {busy && (
          <Spin
            indicator={
              <LoadingOutlined className={css.submitBtnSpinner} spin />
            }
          />
        )}
      </Button>
    </div>
  );
};
const FacebookSettingsStep = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const defaultConfiguration = useSelector(selectXRayConfig);

  const [busy, setBusy] = useState(false);

  const [fbPixelId, setFbPixelId] = useState(
    defaultConfiguration?.pixelId || '',
  );
  const [fbAccessToken, setFbAccessToken] = useState(
    defaultConfiguration?.accessToken || '',
  );
  const [fbApiVersion, setFbApiVersion] = useState('');

  const handleSave = async () => {
    setBusy(true);

    try {
      const res =
        await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
          chatbotId,
          {
            ...defaultConfiguration,
            configurations:
              defaultConfiguration?.configurations || emptyEventsConfig,
            pixelId: fbPixelId,
            accessToken: fbAccessToken,
          },
        );
      if (res.accessToken && res.pixelId) {
        history.replace(
          history.location.pathname.replace('/facebook-settings', '/start'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={css.card}>
      <div className={css.field}>
        <div className={css.fieldName}>
          <span>Meta Pixel ID</span>
          <Tooltip
            css={{ container: css.tooltip }}
            color="dark"
            position="bottom"
            align="center"
            useHover
            withoutLine
            anchorElement={<QuestionCircleOutlined className={css.infoIcon} />}
          >
            Learn more about finding your Meta Pixel ID
            <a className={css.learnMoreLink} href="https://google.com">
              Learn more
            </a>
          </Tooltip>
        </div>

        <Input
          onChange={e => setFbPixelId(e.target.value.trim())}
          disabled={busy}
          value={fbPixelId}
          type="text"
          placeholder="Add Meta Pixel ID"
          css={{ container: css.input }}
        />
      </div>

      <div className={css.field}>
        <div className={css.fieldName}>
          <span>Conversion API Token</span>
          <Tooltip
            css={{ container: css.tooltip }}
            color="dark"
            position="bottom"
            align="center"
            useHover
            withoutLine
            anchorElement={<QuestionCircleOutlined className={css.infoIcon} />}
          >
            Learn more about finding your Conversion API Token
            <a className={css.learnMoreLink} href="https://google.com">
              Learn more
            </a>
          </Tooltip>
        </div>

        <Input
          onChange={e => setFbAccessToken(e.target.value.trim())}
          disabled={busy}
          value={fbAccessToken}
          placeholder="Add Conversion API Token"
          type="text"
          css={{ container: css.input }}
        />
      </div>

      <div className={classNames(css.field, css.hidden)}>
        <div className={css.fieldContainer}>
          <div className={css.fieldName}>
            <span>Facebook API Version</span>
            <Tooltip
              css={{ container: css.tooltip }}
              color="dark"
              position="bottom"
              align="center"
              useHover
              withoutLine
              anchorElement={
                <QuestionCircleOutlined className={css.infoIcon} />
              }
            >
              Facebook API Version Description
            </Tooltip>
          </div>
          <a href="" className={css.whichVersionLink}>
            <FileOutlined />
            Which version should I choose?
          </a>
        </div>
        <Input
          onChange={e => setFbApiVersion(e.target.value.trim())}
          disabled={busy}
          value={fbApiVersion}
          type="text"
          placeholder="Add Facebook API Version"
          css={{ container: css.input }}
        />
      </div>

      <Button
        css={{ container: css.submitBtn }}
        onClick={handleSave}
        disabled={busy || [fbPixelId, fbAccessToken].some(v => !v)}
      >
        Save & Continue{' '}
        {busy && (
          <Spin
            indicator={
              <LoadingOutlined className={css.submitBtnSpinner} spin />
            }
          />
        )}
      </Button>
    </div>
  );
};
const GoLiveStep = () => {
  const [busy, setBusy] = useState(false);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const defaultConfiguration = useSelector(selectXRayConfig);

  const handleStart = async () => {
    setBusy(true);

    try {
      await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
        chatbotId,
        {
          ...defaultConfiguration,
          configurations:
            defaultConfiguration?.configurations || emptyEventsConfig,
          active: !defaultConfiguration?.active,
        },
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={classNames(css.card, css.live)}>
      <ul className={css.checklist}>
        <li className={css.checklistItem}>
          {!!defaultConfiguration?.active && (
            <CheckOutlined className={css.greenIcon} />
          )}

          <span className={css.checklistItemLabel}>
            <strong>Events</strong> are{' '}
            {defaultConfiguration?.active ? 'enabled' : 'disabled'}
          </span>

          <EditOutlined
            className={css.editIcon}
            onClick={() =>
              history.push(history.location.pathname.replace('/start', '/'))
            }
          />
        </li>
      </ul>

      <div className={css.buttons}>
        {defaultConfiguration?.active ? (
          <Button
            color="red"
            css={{ container: classNames(css.submitBtn, css.redBtn) }}
            onClick={handleStart}
            disabled={busy}
          >
            <CaretRightFilled className={css.carretIcon} />
            Stop
          </Button>
        ) : (
          <Button
            color="green"
            css={{ container: classNames(css.submitBtn, css.greenBtn) }}
            onClick={handleStart}
            disabled={busy}
          >
            <CaretRightFilled className={css.carretIcon} />
            Start
          </Button>
        )}

        <Button
          css={{ container: css.testBtn }}
          disabled={!hasConfiguredFb(defaultConfiguration)}
          onClick={async () => {
            if (!hasConfiguredFb(defaultConfiguration)) {
              return;
            }
            try {
              const res =
                await xRayConfigurationCRUDActions.testXRayEventsConfiguration(
                  chatbotId,
                );

              if (res.response.eventsReceived) {
                successToast(`Event received`);
                return;
              }

              if (res?.response?.error?.message) {
                errorToast(
                  `${res.response.error.message} (code ${res.response.error.code})`,
                );
                console.debug(res.response.error);
              }
              // eslint-disable-next-line no-empty
            } finally {
            }
          }}
        >
          Send Test Event
        </Button>
      </div>
    </div>
  );
};

export const SetupForm = ({ className }: SetupFormProps) => {
  const [configReady, setConfigReady] = useState(false);

  const history = useHistory();
  const match = useRouteMatch<{ step?: string; chatbotId: string }>();

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const defaultConfiguration = useSelector(selectXRayConfig);

  const currentStep = Object.values(steps).findIndex(step =>
    step.path.includes(match.params.step || ''),
  );

  const containerClassName = classNames(className, css.container);

  const isDisabled = useCallback(
    step => {
      switch (step.path) {
        case steps.fbSettings.path: {
          return (
            !hasConfiguredEvents(defaultConfiguration) &&
            !hasConfiguredFb(defaultConfiguration)
          );
        }
        case steps.goLive.path: {
          return (
            !hasConfiguredEvents(defaultConfiguration) &&
            !hasConfiguredFb(defaultConfiguration)
          );
        }
        default:
          return false;
        // return Object.values(steps).findIndex(v => v.path === step.path) > currentStep
      }
    },
    [defaultConfiguration, configReady],
  );
  const isDone = useCallback(
    step => {
      switch (step.path) {
        case steps.events.path: {
          return hasConfiguredEvents(defaultConfiguration);
        }
        case steps.fbSettings.path: {
          return hasConfiguredFb(defaultConfiguration);
        }
        case steps.goLive.path: {
          return !!defaultConfiguration?.active;
        }
        default:
          return false;
        // return Object.values(steps).findIndex(v => v.path === step.path) < currentStep
      }
    },
    [configReady, defaultConfiguration],
  );

  const isActive = useCallback(
    step => {
      return (
        Object.values(steps).findIndex(v => v.path === step.path) ===
        currentStep
      );
    },
    [configReady, defaultConfiguration, currentStep],
  );

  useEffect(() => {
    (async () => {
      try {
        await xRayConfigurationCRUDActions.getXRayEventsConfiguration({
          botId: chatbotId,
        });
      } finally {
        setConfigReady(true);
      }
    })();
  }, [chatbotId]);

  return (
    <div className={containerClassName}>
      <div className={css.sidebar}>
        <Button
          css={{ container: css.backBtn }}
          color="transparent"
          onClick={() =>
            history.push(BotBuilderRoutes.restore.getLink({ chatbotId }), {
              options: 'facebook_ads_audience',
            })
          }
        >
          <ArrowLeftOutlined />
          Back to Restore
        </Button>

        <span className={css.sidebarTitle}>Facebook CAPI Setup Steps</span>
        <span className={css.sidebarSubtitle}>Send data to Facebook...</span>

        <ul
          className={classNames(css.stepMenu, {
            [css.disabled]: !configReady,
          })}
        >
          {Object.values(steps).map((step, idx) => (
            <li
              key={step.title}
              className={classNames(css.stepMenuItem, {
                [css.disabled]: isDisabled(step),
                [css.done]: isDone(step),
                [css.active]: isActive(step),
              })}
              onClick={
                isDisabled(step)
                  ? undefined
                  : () => {
                      history.push(
                        match.path
                          .replace(':chatbotId', match.params.chatbotId)
                          .replace(':step?', step.path.replace('/', '')),
                      );
                    }
              }
            >
              <span className={css.stepNumber}>
                {isDone(step) ? <CheckOutlined /> : step.number}
              </span>
              <span className={css.stepTitle}>{step.title}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={css.stepContent}>
        <div className={css.stepContentHeader}>
          <div className={css.stepContentHeaderText}>
            <span className={css.stepContentTitle}>
              {Object.values(steps)[currentStep].title}
            </span>
            <span className={css.stepContentDescription}>
              {Object.values(steps)[currentStep].description}
            </span>
          </div>
          <div className={css.stepActions}>
            <a
              className={css.howToLink}
              href={window.location.href}
              onClick={async e => {
                e.preventDefault();

                // eslint-disable-next-line no-alert
                const shouldReset = window?.confirm?.('Are you sure?');

                if (!shouldReset) {
                  return;
                }

                try {
                  await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
                    chatbotId,
                    {
                      facebookPageId,
                      configurations: emptyEventsConfig,
                      pixelId: '',
                      accessToken: '',
                      active: false,
                    },
                  );
                  window.location.href = BotBuilderRoutes.capi.getLink({
                    chatbotId,
                  });
                  // eslint-disable-next-line no-empty
                } finally {
                }
              }}
            >
              Reset
            </a>

            <a
              href="https://www.facebook.com/business/help/2041148702652965?id=818859032317965"
              target="_blank"
              className={css.howToLink}
            >
              How does Facebook CAPI work?
            </a>
          </div>
        </div>

        {configReady && (
          <Switch>
            <Route
              path={match.path + steps.goLive.path}
              component={GoLiveStep}
            />
            <Route
              path={match.path + steps.fbSettings.path}
              component={FacebookSettingsStep}
            />
            <Route
              path={match.path + steps.events.path}
              exact
              component={EventsStep}
            />
          </Switch>
        )}
      </div>
    </div>
  );
};
